<script>
  import { writable } from 'svelte/store';
  import { goto } from '@sapper/app';

  import gql from 'graphql-tag';
  import { client } from 'utils/apollo.js';

  import validateEmail from '../../utils/validateEmail.js';
  import Swal from 'sweetalert2';

  import Modal from '../Modal.svelte';
  import DesignButton from './DesignButton.svelte';
  import Button from '../Button.svelte';
  import Radio from '../Radio.svelte';
  import Input from '../Input.svelte';
  import ColoredButton from './ColoredButton.svelte';
  import BulkPricingModal from '../BulkPricingModal.svelte';

  import { productDataStore } from '../../stores/ProductData.js';
  import { designState } from '../../stores/Designer.js';
  import { teamDetails } from '../../stores/TeamDetails.js';
  import { konvaStageId } from '../../stores/Preview.js'; // used to determine design element in designState
  import { savedDesign } from '../../stores/SavedDesign.js';
  import { environment } from '../../environment/environment.js';

  import determineDeliveryDate from '../../utils/determineDeliveryDate.js';
  import DesignSellingPoints from './DesignSellingPoints.svelte';

  export let productName = '';
  export let productSlug = '';
  export let currentStep;
  export let designSteps;
  export let showFloatingProgress = false;

  let showPricingModal = false;

  let arrayOfSelectedProducts = designState.arrayOfSelectedProducts($konvaStageId);

  function handleShowPricingModal() {
    arrayOfSelectedProducts = designState.arrayOfSelectedProducts($konvaStageId);
    showPricingModal = true;
  }

  // Create some state for the save/share modal
  let showSaveModal = writable(false);
  let saveModalState = writable('launch'); // Other states are launch, saved, success, error

  const productTypesArray = environment.PRODUCT_TYPES;

  let designIndex;
  $: if ($konvaStageId) {
    designIndex = $designState.findIndex((o) => o.id === $konvaStageId);
  }

  let nextLabel;
  $: if (designSteps[currentStep.stepNumber] && designSteps[currentStep.stepNumber].label) {
    nextLabel = designSteps[currentStep.stepNumber].label;
  } else {
    nextLabel = '';
  }

  async function changeStep(direction) {
    let url;
    let nextIndex;
    let slug;
    if ($productDataStore.top) {
      slug = $productDataStore.top.slug;
    } else {
      slug = $productDataStore[$productDataStore.showing].slug;
    }
    if (direction === 'prev') {
      nextIndex = currentStep.stepNumber - 2;
    } else {
      nextIndex = currentStep.stepNumber;
    }
    if (nextIndex >= 0 && nextIndex <= designSteps.length - 1) {
      let nextStep = designSteps[nextIndex];
      url = '/design/' + slug + '/' + nextStep.segment;
      await goto(url, { noscroll: true });
      currentStep = designSteps[nextIndex];
      showFloatingProgress = false; // If in mobile mode close menu on step change
    } else {
      return;
    }
  }

  // Determine delivery times for product
  // Sometimes product data store showing is empty. e.g. when switching from choose colours to add shorts
  // In this case we need to use the top or next available item for delivery times
  let deliveryTimes = [];
  function determineProductDeliveryTimes(productData) {
    let deliveryTimes = [];
    if (productData.showing && productData[productData.showing]?.deliveryTimes) {
      deliveryTimes = productData[productData.showing].deliveryTimes;
    } else {
      for (const type of productTypesArray) {
        if (productData[type] && productData[type].deliveryTimes) {
          deliveryTimes = productData[type].deliveryTimes;
          break;
        }
      }
    }
    return deliveryTimes;
  }

  $: {
    deliveryTimes = determineProductDeliveryTimes($productDataStore);
  }

  function moreProductInformation() {
    goto('/product/' + productSlug);
  }

  let designPreviewImage = null;
  let previousSavedDesign;

  async function sendSaveOrderEmail(email, saveId, saveName, saveImage, saveLink) {
    const SAVE_ORDER_EMAIL_QUERY = gql`
      query MyQuery($email: String!, $saveId: String!, $saveImage: String!, $saveLink: String!, $saveName: String!) {
        saveOrderEmail(
          email: $email
          saveId: $saveId
          saveImage: $saveImage
          saveLink: $saveLink
          saveName: $saveName
        ) {
          status
        }
      }
    `;

    const response = await client.query({
      query: SAVE_ORDER_EMAIL_QUERY,
      variables: {
        email: email,
        saveId: saveId,
        saveImage: saveImage,
        saveLink: saveLink,
        saveName: saveName,
      },
    });

    return response.data.saveOrderEmail.status;
  }

  async function generateDesignPreview(productType) {
    return await designState.generateDesignPreview({
      designId: $konvaStageId,
      item: productType,
      side: 'front',
      gender: $productDataStore[$productDataStore.showing].gender,
      isModelBasedImages: $productDataStore[$productDataStore.showing].isModelBasedImages,
    });
  }

  async function launchSaveDesign() {
    // First check if design has been saved before - only need to do once
    if ($savedDesign.previouslySaved === false) {
      previousSavedDesign = await designState.saveExists($konvaStageId);

      if (previousSavedDesign) {
        // Now set all the store values from the previous saved design response
        $savedDesign.previouslySaved = true;
        $savedDesign.name = previousSavedDesign.saveName;
        $savedDesign.email = previousSavedDesign.userEmail;
        $savedDesign.identifier = previousSavedDesign.identifier;
        $savedDesign.mode = 'replace';
      } else {
        $savedDesign.mode = 'new'; // Create a new save design
      }
    }

    let arrayOfSelectedProducts = designState.arrayOfSelectedProducts($konvaStageId);

    // check which products exist and set most prominent as preview
    let productType = 'top';
    if (arrayOfSelectedProducts.includes('accessory')) {
      productType = 'accessory';
    }
    if (arrayOfSelectedProducts.includes('socks')) {
      productType = 'socks';
    }
    if (arrayOfSelectedProducts.includes('hat')) {
      productType = 'hat';
    }
    if (arrayOfSelectedProducts.includes('bottom')) {
      productType = 'bottom';
    }
    if (arrayOfSelectedProducts.includes('top')) {
      productType = 'top';
    }

    designPreviewImage = await generateDesignPreview(productType);

    // show modal with option to update save name
    // or save as new design
    $showSaveModal = true;
  }

  let savedId = null;
  async function saveDesign() {
    if ($savedDesign.mode === 'replace') {
      // Need to overwrite the saved design
      savedId = await designState.saveDesign({
        designId: $konvaStageId,
        teamDetails: $teamDetails,
        gender: $productDataStore[$productDataStore.showing].gender,
        arrayOfSelectedProducts: designState.arrayOfSelectedProducts($konvaStageId),
        saveName: $savedDesign.name,
        userEmail: $savedDesign.email,
        identifier: $savedDesign.designId,
      });
      let link = process.env.SITE_URL + '/design/load/' + savedId;
      sendSaveOrderEmail($savedDesign.email, savedId, $savedDesign.name, designPreviewImage, link); // No need to await

      $saveModalState = 'saved';
    } else {
      // Add new saved design
      // First need to check if a name has been entered
      let inputValid = true;
      if ($savedDesign.name === '') {
        Swal.fire('Invalid Name', 'Please enter a name for your design.', 'error');
        inputValid = false;
      }
      // Validate email
      if (!validateEmail($savedDesign.email)) {
        Swal.fire('Invalid Email', 'Please enter a valid email address.', 'error');
        inputValid = false;
      }

      if (inputValid) {
        savedId = await designState.saveDesign({
          designId: $konvaStageId,
          teamDetails: $teamDetails,
          gender: $productDataStore[$productDataStore.showing].gender,
          arrayOfSelectedProducts: designState.arrayOfSelectedProducts($konvaStageId),
          saveName: $savedDesign.name,
          userEmail: $savedDesign.email,
        });

        let link = process.env.SITE_URL + '/design/load/' + savedId;
        sendSaveOrderEmail($savedDesign.email, savedId, $savedDesign.name, designPreviewImage, link); // No need to await
        // Now set values in savedDesign store
        $savedDesign.previouslySaved = true;
        $savedDesign.name = $savedDesign.name;
        $savedDesign.email = $savedDesign.email;
        $savedDesign.identifier = savedId;
        $savedDesign.mode = 'replace';
        $savedDesign.designId = $konvaStageId;

        // Set state to saved
        $saveModalState = 'saved';
      }
    }
  }
</script>

<Modal bind:show={$showSaveModal} allowClose={true} closeFn={() => ($saveModalState = 'launch')}>
  <div class="modal-container">
    <div class="modal-header">
      <h2>Save Your Design</h2>
      <div class="modal-sub-heading">View on Any Device. Share with your Team!</div>
    </div>
    <div class="modal-body">
      <div class="modal-body-content">
        {#if $saveModalState === 'launch'}
          <img class="modal-preview-image" src={designPreviewImage} alt="" />
          <div class="modal-detail-content">
            {#if $savedDesign.previouslySaved === true}
              <div class="radio">
                <Radio
                  label={'Replace: "' + $savedDesign.name + '"'}
                  bind:group={$savedDesign.mode}
                  value={'replace'}
                />
              </div>
              <div class="radio">
                <Radio label={'Enter a New Name'} bind:group={$savedDesign.mode} value={'new'} />
              </div>

              {#if $savedDesign.mode === 'new'}
                <div class="modal-name-input">
                  <Input placeholder={$savedDesign.name} bind:value={$savedDesign.name} />
                </div>
              {/if}
            {:else}
              <Input placeholder={'Enter a Name'} bind:value={$savedDesign.name} labelNote={'A name for your design'} />
              <Input
                type={'email'}
                placeholder={'Enter your email'}
                labelNote={'We will email you a link to your design'}
                bind:value={$savedDesign.email}
              />
            {/if}
          </div>
        {/if}

        {#if $saveModalState === 'saved'}
          <div class="modal-save-url-content">
            <div class="modal-save-link-header">Your Saved Design Link:</div>
            <div class="modal-save-link">
              {process.env.SITE_URL + '/design/load/' + savedId}
            </div>
          </div>
        {/if}
      </div>
    </div>

    <div class="modal-footer">
      <div class="modal-button">
        {#if $saveModalState !== 'saved'}
          <Button label="Save" width="inherit" clickFn={saveDesign} />
        {:else}
          <Button
            label="Close"
            width="inherit"
            clickFn={() => {
              $showSaveModal = false;
              $saveModalState = 'launch';
            }}
          />
        {/if}
      </div>
    </div>
  </div>
</Modal>

<div class="details" id="details" style="">
  <div class="header">
    <h3>{productName}</h3>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <span class="more-information" on:click={moreProductInformation}>i</span>
  </div>

  <DesignSellingPoints />
  <div class="view-pricing">
    <ColoredButton label="View Pricing Details" type="green" clickFn={handleShowPricingModal} />
  </div>
  {#if showPricingModal}
    <BulkPricingModal bind:show={showPricingModal} {arrayOfSelectedProducts} />
  {/if}
  <div class="estimated-shipping">
    <strong>Estimated Shipping</strong>
    {#each deliveryTimes as deliveryTime}
      <div class="delivery-time">
        {deliveryTime.DeliveryTime.name} - {determineDeliveryDate(deliveryTime.DeliveryTime.deliveryTime)}
      </div>
    {/each}
  </div>
  <div class="buttons">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <span class="arrow" on:click={() => changeStep('prev')}>
      <DesignButton label="Go Back" disabled={currentStep.stepNumber === 1} />
    </span>

    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <span class="arrow" on:click={() => changeStep('next')}>
      <DesignButton label="Next: {nextLabel}" disabled={currentStep.stepNumber === designSteps.length} />
    </span>
  </div>

  <div class="buttons">
    {#if $designState.mode !== 'edit'}
      <!-- <ColoredButton label="Share" clickFn={saveDesign} /> -->
      <ColoredButton label="Save/Share Design" type="green" clickFn={launchSaveDesign} />
    {/if}
  </div>
</div>

<style>
  .details {
    padding: 2rem;
    background-color: #fff;
    border-radius: 5px;
    top: 7rem;
    box-shadow: 1px 1px 0.5rem rgba(0, 0, 0, 0.15);
  }

  .arrow {
    display: block;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid rgba(0, 0, 0, 0.2);
    padding-top: 1rem;
    padding-bottom: 2rem;
  }

  .header h3 {
    font-size: 1.9rem;
    text-transform: capitalize;
  }

  .more-information {
    cursor: pointer;
  }

  .header span {
    width: 1.6rem;
    height: 1.6rem;
    font-size: 1rem;
    font-weight: 700;
    border-radius: 4rem;
    background-color: #fe5900;
    color: #fff;
    display: flex;
    font-family: 'Oswald', sans-serif;
    justify-content: center;
    align-items: center;
  }

  /* .progress {
    padding: 1.5rem 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.2);
  }

  .progress strong {
    color: #1dab56;
  }

  .progress .step {
    margin-top: 0.8rem;
    color: #666;
    display: flex;
    align-items: center;
    font-size: 1.4rem;
    text-decoration: none;
    cursor: pointer;
  }

  .progress .step.active {
    color: #000;
  }

  .progress .step .check {
    width: 1.6rem;
    height: 1.6rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.25);
    color: #fff;
    border-radius: 5rem;
    margin-right: 0.8rem;
    font-weight: 600;
  }

  .progress .step.active .check {
    background-color: #fe5900;
  } */

  .options {
    padding: 1.5rem 0 0 0;
  }

  .options .title {
    color: #666;
  }

  .options .total {
    border-top: 1px solid rgba(0, 0, 0, 0.15);
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
    padding: 1rem 0;
    margin: 2.5rem 0 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .options .total strong {
    font-size: 1.6rem;
  }

  .options .total .price {
    font-family: 'Oswald', sans-serif;
    font-weight: 600;
    color: #fe5900;
    font-size: 2.2rem;
  }

  .estimated-shipping {
    padding: 2rem 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
    font-size: 1.4rem;
  }

  .delivery-time {
    margin-top: 0.8rem;
    color: #666;
    text-transform: capitalize;
    font-size: 1.4rem;
  }

  .buttons {
    justify-content: center;
    display: flex;
    gap: 2rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
    padding: 1rem 0;
  }

  .modal-container {
    text-align: center;
  }

  .modal-header {
    font-family: 'Oswald';
    font-weight: 700;
    color: #4a4a4a;
    text-transform: uppercase;
    font-size: 1.4rem;
    margin-bottom: 2rem;
    text-align: center;
  }

  .modal-sub-heading {
    text-transform: capitalize;
    font-family: 'Inter';
    font-weight: 500;
  }

  .modal-preview-image {
    margin-right: 5rem;
  }

  .modal-body-content {
    display: flex;
  }

  .modal-detail-content {
    margin-top: 5rem;
    min-width: 25rem;
  }

  .modal-name-input {
    padding-top: 3rem;
  }

  .radio {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }

  .modal-save-url-content {
    width: 100%;
  }

  .modal-save-link-header {
    text-transform: capitalize;
    font-family: 'Inter';
    font-weight: 500;
    font-size: 1.4rem;
    margin-bottom: 1rem;
  }

  .modal-save-link {
    font-size: 1.3rem;
  }

  .modal-footer {
    margin-top: 2rem;
    display: flex;
    align-items: center;
    justify-content: space-evenly;
  }

  .view-pricing {
    margin-top: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  }
</style>
